// TrialBanner — десктоп: карточка в правом нижнем углу, mobile: полоска <3 дней до конца.
// Spec: docs/ui-spec/billing.trial.banner.{desktop,mobile}.md
//
// Источник — ПРАВА (`iecId.entitlements.get`), а не сессия. Прежняя версия
// читала `session.user.billing?.trialEndsAt`, а поля `billing` в `SessionUser`
// нет вовсе — баннер включался только через `?demo=` и в проде не показывался
// никогда. После переезда пробный выдаёт экосистема, и единственный признак —
// действующее право с `source: "trial"`.
//
// Текст «дальше автопродление за столько-то» убран намеренно: автопродления в
// экосистеме нет, пробный просто заканчивается и человек оказывается на
// бесплатном тарифе (гайд §4.6). Обещать списание было бы враньём.

import { Button } from "@studyhub/ui/components/button";
import { IconSparkles, IconX } from "@tabler/icons-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { useIsLgUp } from "@/lib/platform-detect";
import { api } from "@/lib/trpc-client";
import { useSessionStore } from "@/stores/session";
import { useSupportWidgetObstruction } from "@/stores/support-widget";
import { resolveGoverningEntitlement } from "../_entitlement";

const DISMISS_KEY = "studyhub:trial-banner:dismissed-at";

export function TrialBanner() {
	const { t } = useTranslation("billing");
	const isDesktop = useIsLgUp();
	const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	// Баннер смонтирован в `__root` на всё приложение — запрос гейтим по
	// авторизации и держим редким: право меняется событием, не опросом.
	const entitlements = api.iecId.entitlements.get.useQuery(undefined, {
		enabled: isAuthenticated,
		staleTime: 5 * 60_000,
	});

	const [dismissed, setDismissed] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const v = localStorage.getItem(DISMISS_KEY);
		if (v && Date.now() - Number(v) < 24 * 3600_000) setDismissed(true);
	}, []);

	const governing = resolveGoverningEntitlement(
		entitlements.data?.items ?? [],
		entitlements.data?.serviceTier ?? "free",
	);
	const trialEndsAt = governing?.source === "trial" ? governing.expiresAt : null;
	const isHiddenRoute = pathname.startsWith("/billing/") || pathname.startsWith("/onboarding");
	// Карточка на компьютере стоит в правом нижнем углу — там же кнопка «Поддержка».
	useSupportWidgetObstruction(isDesktop && !!trialEndsAt && !dismissed && !isHiddenRoute);

	if (isHiddenRoute) return null;
	if (!trialEndsAt) return null;
	if (dismissed) return null;

	const daysLeft = Math.max(0, Math.ceil((Date.parse(trialEndsAt) - Date.now()) / 86_400_000));

	// Mobile sticky показывается только <3 дней.
	if (!isDesktop && daysLeft >= 3) return null;

	const tierLabel = governing?.tier === "pro" ? "Pro" : "Plus";

	const onDismiss = (): void => {
		if (typeof window !== "undefined") localStorage.setItem(DISMISS_KEY, String(Date.now()));
		setDismissed(true);
	};

	if (isDesktop) {
		return (
			<div className="fixed bottom-6 right-6 z-30 w-[320px] rounded-[var(--radius-panel)] border border-warning/30 bg-card p-4 shadow-e3">
				<div className="flex items-start gap-3">
					<IconSparkles size={20} className="shrink-0 text-warning" />
					<div className="min-w-0 flex-1">
						<div className="text-sm font-semibold text-foreground">
							{t("iec.trial.bannerTitle", { tier: tierLabel })}
						</div>
						<div className="mt-0.5 text-xs tabular-nums text-foreground/80">
							{t("trial.daysLeft", { count: daysLeft })}
						</div>
						<div className="mt-2 text-xs text-muted-foreground">
							{t("iec.trial.bannerSubtitle")}
						</div>
						<div className="mt-3">
							<Button asChild size="sm" className="cursor-pointer">
								<Link to="/settings/billing" search={{}}>
									{t("iec.trial.bannerCta")}
								</Link>
							</Button>
						</div>
					</div>
					<button
						type="button"
						onClick={onDismiss}
						className="cursor-pointer rounded-md p-1 text-muted-foreground transition-colors duration-fast hover:bg-muted hover:text-foreground"
						aria-label={t("trial.dismiss")}
					>
						<IconX size={14} />
					</button>
				</div>
			</div>
		);
	}

	return (
		<div
			className={cn(
				"flex items-center gap-3 border-b border-warning/30 bg-warning/10 px-4 py-2.5 text-sm",
			)}
			// См. `BannerStack`: первый баннер стопки получает padding-top не ниже
			// собственного (иначе `py-2.5` перезаписывался бы нулём).
			style={{ "--sh-banner-pt": "0.625rem" } as React.CSSProperties}
		>
			<IconSparkles size={16} className="shrink-0 text-warning" />
			<div className="min-w-0 flex-1">
				<div className="text-xs font-medium text-foreground">
					{t("iec.trial.bannerTitle", { tier: tierLabel })} —{" "}
					<span className="tabular-nums">{t("trial.daysLeft", { count: daysLeft })}</span>
				</div>
			</div>
			<Button asChild size="sm" variant="outline" className="h-8 cursor-pointer">
				<Link to="/settings/billing" search={{}}>
					{t("iec.trial.bannerCta")}
				</Link>
			</Button>
			<button
				type="button"
				onClick={onDismiss}
				className="cursor-pointer rounded-md p-1.5 text-muted-foreground transition-colors duration-fast hover:bg-muted hover:text-foreground"
				aria-label={t("trial.dismiss")}
			>
				<IconX size={16} />
			</button>
		</div>
	);
}
